import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { getTransactions, getUserById, getUsers, getAdminFundStats, getAdminFundBreakdown, getAdminOfferStats, getMerchantStatusSummary, getFundraiserStatusSummary } from "../../services/adminService";
import toast from "../../utils/toast";

const initialState = {
  allUsers: null,
  transactions: null,
  userById: null,
  dashboardFundStats: null,
  dashboardFundBreakdown: null,
  dashboardOfferStats: null,
  merchantStatusSummary: null,
  fundraiserStatusSummary: null,
  loading: false,
};

const adminSlice = createSlice({
  name: "admin",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getUsers?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.allUsers = action.payload.data;
      } else {
        // Deduplicate toasts
        const msg = action?.payload?.message;
        if (msg) {
          if (typeof window !== 'undefined') {
            const now = Date.now();
            const last = window.__LAST_ERROR_TOAST__ || { msg: null, at: 0 };
            if (last.msg !== msg || now - last.at > 4000) {
              toast.error(msg);
              window.__LAST_ERROR_TOAST__ = { msg, at: now };
            }
          } else {
            toast.error(msg);
          }
        }
      }
      state.loading = false;
    });

    builder.addCase(getTransactions?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.transactions = action.payload;
      } else {
        const msg = action?.payload?.message;
        if (msg) {
          if (typeof window !== 'undefined') {
            const now = Date.now();
            const last = window.__LAST_ERROR_TOAST__ || { msg: null, at: 0 };
            if (last.msg !== msg || now - last.at > 4000) {
              toast.error(msg);
              window.__LAST_ERROR_TOAST__ = { msg, at: now };
            }
          } else {
            toast.error(msg);
          }
        }
      }
      state.loading = false;
    });

    builder.addCase(getUserById?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.userById = action.payload.data;
      } else {
        const msg = action?.payload?.message;
        if (msg) {
          if (typeof window !== 'undefined') {
            const now = Date.now();
            const last = window.__LAST_ERROR_TOAST__ || { msg: null, at: 0 };
            if (last.msg !== msg || now - last.at > 4000) {
              toast.error(msg);
              window.__LAST_ERROR_TOAST__ = { msg, at: now };
            }
          } else {
            toast.error(msg);
          }
        }
      }
      state.loading = false;
    });

    // Dashboard stats fulfilled handlers
    builder.addCase(getAdminFundStats?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.dashboardFundStats = action.payload.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });
    builder.addCase(getAdminFundBreakdown?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.dashboardFundBreakdown = action.payload.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });
    builder.addCase(getAdminOfferStats?.fulfilled, (state, action) => {
      console.log('adminSlice - getAdminOfferStats fulfilled:', action?.payload);
      if (action?.payload?.success === true) {
        console.log('adminSlice - Setting dashboardOfferStats:', action.payload.data);
        state.dashboardOfferStats = action.payload.data;
      } else {
        console.log('adminSlice - getAdminOfferStats failed:', action?.payload?.message);
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    // Status summaries
    builder.addCase(getMerchantStatusSummary?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.merchantStatusSummary = action.payload.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });
    builder.addCase(getFundraiserStatusSummary?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.fundraiserStatusSummary = action.payload.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder
      .addMatcher(
        isAnyOf(getUsers?.pending, getTransactions?.pending, getUserById?.pending, getAdminFundStats?.pending, getAdminFundBreakdown?.pending, getAdminOfferStats?.pending, getMerchantStatusSummary?.pending, getFundraiserStatusSummary?.pending),
        (state) => {
          state.loading = true;
        }
      )
      .addMatcher(
        isAnyOf(getUsers?.rejected, getTransactions?.rejected, getUserById?.rejected, getAdminFundStats?.rejected, getAdminFundBreakdown?.rejected, getAdminOfferStats?.rejected, getMerchantStatusSummary?.rejected, getFundraiserStatusSummary?.rejected),
        (state, action) => {
          state.loading = false;
          // Allow callers to silence error toasts
          if (action?.meta?.arg?.silent === true) {
            return;
          }
          
          // Don't show toasts for permission errors (403) as these are expected for non-admin users
          const status = action?.payload?.status || action?.error?.status;
          if (status === 403 || status === "403") {
            return;
          }
          
          const msg = action?.payload?.message || action?.error?.message || "Something went wrong";
          if (typeof window !== 'undefined') {
            const now = Date.now();
            const last = window.__LAST_ERROR_TOAST__ || { msg: null, at: 0 };
            if (last.msg !== msg || now - last.at > 4000) {
              toast.error(msg);
              window.__LAST_ERROR_TOAST__ = { msg, at: now };
            }
          } else {
            toast.error(msg);
          }
        }
      );
  },
});

// No actions exported from this slice
export default adminSlice?.reducer;
